// LLMLite setup wizard — flow:
//   1) Prompt API key ẩn (password input, không echo)
//   2) Prompt base URL (default https://ai.nal.vn)
//   3) Fetch GET /v1/models verify key + lấy danh sách model
//   4) Smart picker: auto-pick nếu chỉ 1 claude-alias, prompt nếu nhiều, fallback toàn list
// Output: { apiKey, baseUrl, model } cho phase 06 write settings.json.
import { inputOrFlag, passwordOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";

export interface LLMLiteConfig {
  apiKey: string;
  baseUrl: string;
  model: string;
  // Danh sách model key được phép dùng (từ /v1/models). Dùng lại để quyết định
  // có hỏi ai-orchestrator không (cần >1 model) — tránh fetch /v1/models 2 lần.
  availableModels: string[];
}

// Endpoint mặc định NAL host. User có thể override (vd self-hosted dev).
const DEFAULT_BASE_URL = "https://ai.nal.vn";

// 10s timeout fetch — VN mạng có thể chậm nhưng 10s đủ cho list models (response < 1KB).
const FETCH_TIMEOUT_MS = 10_000;

// Trả "sk-...XXXX" để log không expose full token nhưng vẫn distinguishable.
export function maskApiKey(key: string): string {
  if (key.length <= 8) return "sk-***";
  return `${key.slice(0, 3)}...${key.slice(-4)}`;
}

/** Cờ điền sẵn cho luồng non-interactive (T1). Key CHỈ đến từ stdin, không từ argv. */
export interface LLMLiteSetupOptions {
  /** Đã đọc từ stdin qua `--llmlite-api-key-stdin`. */
  apiKey?: string;
  /** Từ `--llmlite-base-url`. */
  baseUrl?: string;
  /** Từ `--llmlite-model`. */
  model?: string;
}

const KEY_FLAG_SPEC = {
  flag: "--llmlite-api-key-stdin",
  command: "ai setup",
  hint: 'echo "$KEY" | avatar ai setup --non-interactive --ai-provider llmlite --llmlite-api-key-stdin …',
} as const;

const BASE_URL_FLAG_SPEC = {
  flag: "--llmlite-base-url",
  command: "ai setup",
  hint: "--llmlite-base-url https://ai.nal.vn",
} as const;

const MODEL_FLAG_SPEC = {
  flag: "--llmlite-model",
  command: "ai setup",
  hint: "--llmlite-model nal-claude-sonnet",
} as const;

// Prompt ẩn — `password` không echo input, hiển thị `*`.
async function promptApiKeyHidden(provided?: string): Promise<string> {
  return await passwordOrFlag(provided, KEY_FLAG_SPEC, {
    message: "LLMLite API key (ẩn input):",
    mask: "*",
    validate: (v) => (v.trim().length > 0 ? true : "API key bắt buộc"),
  });
}

// Prompt base URL với default cho user enter-to-accept nhanh.
async function promptBaseUrl(
  provided?: string,
  defaultUrl: string = DEFAULT_BASE_URL,
): Promise<string> {
  // URL qua cờ KHÔNG đi qua `validate` của inquirer ⇒ kiểm tay, nếu không thì
  // `--llmlite-base-url ai.nal.vn` (thiếu scheme) chỉ chết ở tầng fetch.
  if (provided !== undefined && provided !== "" && !/^https?:\/\//.test(provided)) {
    throw new Error(`--llmlite-base-url "${provided}" phải là URL http/https hợp lệ`);
  }
  const value = await inputOrFlag(provided, BASE_URL_FLAG_SPEC, {
    message: "LLMLite base URL:",
    default: defaultUrl,
    validate: (v) => (/^https?:\/\//.test(v) ? true : "Phải là URL hợp lệ (http/https)"),
  });
  // Strip trailing slash để concat /v1/... predictable.
  return value.replace(/\/+$/, "");
}

// Shape response /v1/models theo OpenAI spec — chỉ trust field `id`.
interface ModelsResponse {
  data?: Array<{ id?: unknown }>;
}

// Fetch danh sách model từ endpoint. Throws có message rõ để caller hiển thị.
export async function fetchAvailableModels(baseUrl: string, apiKey: string): Promise<string[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${baseUrl}/v1/models`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (res.status === 401 || res.status === 403) {
      throw new Error(`API key invalid (HTTP ${res.status}).`);
    }
    if (res.status === 404) {
      throw new Error(`Endpoint /v1/models không tồn tại trên ${baseUrl}.`);
    }
    if (!res.ok) {
      throw new Error(`Fetch models thất bại (HTTP ${res.status}).`);
    }

    const json = (await res.json()) as ModelsResponse;
    const models = (json.data || [])
      .map((m) => (typeof m.id === "string" ? m.id : null))
      .filter((id): id is string => id !== null);

    if (models.length === 0) {
      throw new Error("LLMLite trả về list rỗng. Liên hệ admin NAL.");
    }
    return models;
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      // v1.14.2 UX: NAL gateway (ai.nal.vn) là internal endpoint — chỉ accessible qua VPN NAL.
      // Common case: user quên bật VPN → timeout. Hint actionable thay vì chỉ "timeout".
      const isNalGateway = baseUrl.includes("nal.vn") || baseUrl.includes("nal-vn");
      const vpnHint = isNalGateway
        ? "\n  Hint: ai.nal.vn là internal endpoint NAL — kiểm tra VPN NAL đã bật chưa, hoặc tcp test bằng `curl -v https://ai.nal.vn`."
        : "\n  Hint: check mạng / firewall / VPN, hoặc base URL có đúng không.";
      throw new Error(`Connect ${baseUrl} timeout sau ${FETCH_TIMEOUT_MS / 1000}s.${vpnHint}`);
    }
    // Network error khác (DNS fail, refused, ...) — log raw error.
    const errMsg = (err as Error).message || String(err);
    if (errMsg.toLowerCase().includes("fetch failed") || errMsg.includes("ENOTFOUND")) {
      const isNalGateway = baseUrl.includes("nal.vn");
      const vpnHint = isNalGateway ? " (ai.nal.vn cần VPN NAL — kiểm tra VPN đã bật chưa)" : "";
      throw new Error(`Network error khi connect ${baseUrl}: ${errMsg}${vpnHint}`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Smart picker: auto-pick nếu chỉ 1 claude-alias, prompt nếu nhiều, fallback all.
// `claude` substring match cover: nal-claude, claude-sonnet-4-5, claude-3-5-sonnet, ...
export async function promptModelChoice(models: string[], preset?: string): Promise<string> {
  // Cờ chỉ định model: kiểm nó CÓ THẬT trên endpoint trước khi ghi vào settings.
  if (preset !== undefined && preset !== "") {
    if (!models.includes(preset)) {
      throw new Error(
        `Model "${preset}" không có trên endpoint. Model khả dụng: ${models.join(", ")}`,
      );
    }
    return preset;
  }

  const claudeAliases = models.filter((m) => m.toLowerCase().includes("claude"));

  if (claudeAliases.length === 1) {
    // length === 1 đã guard → claudeAliases[0] non-null.
    const only = claudeAliases[0]!;
    log.info(`Auto-pick model: ${only} (chỉ 1 claude alias trên endpoint)`);
    return only;
  }

  const choiceList = claudeAliases.length > 0 ? claudeAliases : models;
  return await selectOrFlag(preset, MODEL_FLAG_SPEC, {
    message: "Chọn model mặc định cho project:",
    choices: choiceList.map((m) => ({ name: m, value: m })),
  });
}

// Main wizard. Orchestrate 4 step.
export async function setupLLMLiteApiKeyAndModel(
  options: LLMLiteSetupOptions = {},
): Promise<LLMLiteConfig> {
  const apiKey = await promptApiKeyHidden(options.apiKey);
  const baseUrl = await promptBaseUrl(options.baseUrl);

  log.info(`Verify key (${maskApiKey(apiKey)}) qua ${baseUrl}/v1/models...`);
  const models = await fetchAvailableModels(baseUrl, apiKey);
  log.success(`Endpoint OK — ${models.length} models available`);

  const model = await promptModelChoice(models, options.model);
  return { apiKey, baseUrl, model, availableModels: models };
}
